import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

class StickyATCComponent extends Component {
  requiredRefs = ['barRoot', 'addToCartButton'];

  connectedCallback() {
    super.connectedCallback();

    this.sectionId = this.dataset.sectionId || '';
    this.productId = this.dataset.productId || '';
    this.stickyMode = this.dataset.stickyAtcMode || 'desktop_and_mobile';
    this.stickyShow = this.dataset.stickyAtcShow || 'after_form_scroll';
    this.isClosed = false;

    const sectionRootId = this.sectionId ? `shopify-section-${this.sectionId}` : null;
    this.section = (sectionRootId && document.getElementById(sectionRootId)) || this.closest('.shopify-section') || document;

    this.mainForm = this.section.querySelector('product-form-component form[data-type="add-to-cart-form"]');
    this.mainQuantityInput = this.mainForm?.querySelector('input[name="quantity"]') || null;

    const primaryAtcButton = this.section.querySelector('.buy-buttons-block .add-to-cart-button');
    this.triggerElement = primaryAtcButton || this.section.querySelector('.buy-buttons-block') || this.mainForm;

    this.hasSeenTrigger = false;
    this.hasUserScrolled = false;

    this.#bindHandlers();

    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);

    this.section.addEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
    this.section.addEventListener(ThemeEvents.quantitySelectorUpdate, this.handleQuantitySelectorUpdate);

    if (this.refs.quantityMinus && this.refs.quantityMinus instanceof HTMLElement) {
      this.refs.quantityMinus.addEventListener('click', this.handleQuantityMinusClick);
    }
    if (this.refs.quantityPlus && this.refs.quantityPlus instanceof HTMLElement) {
      this.refs.quantityPlus.addEventListener('click', this.handleQuantityPlusClick);
    }
    if (this.refs.quantityInput && this.refs.quantityInput instanceof HTMLInputElement) {
      this.refs.quantityInput.addEventListener('change', this.handleQuantityInputChange);
    }

    if (this.refs.addToCartButton instanceof HTMLElement) {
      this.refs.addToCartButton.addEventListener('click', this.handleStickySubmit);
    }
    if (this.refs.closeButton && this.refs.closeButton instanceof HTMLElement) {
      this.refs.closeButton.addEventListener('click', this.handleCloseClick);
    }

    this.syncInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);

    if (this.section) {
      this.section.removeEventListener(ThemeEvents.variantUpdate, this.handleVariantUpdate);
      this.section.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.handleQuantitySelectorUpdate);
    }

    if (this.refs.quantityMinus && this.refs.quantityMinus instanceof HTMLElement) {
      this.refs.quantityMinus.removeEventListener('click', this.handleQuantityMinusClick);
    }
    if (this.refs.quantityPlus && this.refs.quantityPlus instanceof HTMLElement) {
      this.refs.quantityPlus.removeEventListener('click', this.handleQuantityPlusClick);
    }
    if (this.refs.quantityInput && this.refs.quantityInput instanceof HTMLInputElement) {
      this.refs.quantityInput.removeEventListener('change', this.handleQuantityInputChange);
    }

    if (this.refs.addToCartButton instanceof HTMLElement) {
      this.refs.addToCartButton.removeEventListener('click', this.handleStickySubmit);
    }
    if (this.refs.closeButton && this.refs.closeButton instanceof HTMLElement) {
      this.refs.closeButton.removeEventListener('click', this.handleCloseClick);
    }
  }

  #bindHandlers() {
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.handleQuantitySelectorUpdate = this.handleQuantitySelectorUpdate.bind(this);
    this.handleQuantityMinusClick = this.handleQuantityMinusClick.bind(this);
    this.handleQuantityPlusClick = this.handleQuantityPlusClick.bind(this);
    this.handleQuantityInputChange = this.handleQuantityInputChange.bind(this);
    this.handleStickySubmit = this.handleStickySubmit.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
  }

  handleScroll() {
    this.hasUserScrolled = true;
    this.updateVisibility();
  }

  handleResize() {
    this.updateVisibility();
  }

  shouldShowForViewport() {
    if (this.stickyMode === 'disabled') return false;

    const width = window.innerWidth || document.documentElement.clientWidth;
    if (this.stickyMode === 'desktop_only') {
      return width >= 750;
    }

    return true;
  }

  updateVisibility() {
    const root = this.refs.barRoot;
    if (!(root instanceof HTMLElement)) return;

    if (this.isClosed || !this.shouldShowForViewport()) {
      root.classList.remove('sticky-atc--visible');
      return;
    }

    const winHeight = window.innerHeight || document.documentElement.clientHeight;
    const docHeight = document.documentElement.scrollHeight;
    const scrollY = window.scrollY || window.pageYOffset;
    const totalScroll = scrollY + winHeight;
    const atBottom = totalScroll >= docHeight - 60;

    if (atBottom) {
      root.classList.remove('sticky-atc--visible');
      return;
    }

    if (this.stickyShow === 'always') {
      if (this.hasUserScrolled) {
        root.classList.add('sticky-atc--visible');
      } else {
        root.classList.remove('sticky-atc--visible');
      }
      return;
    }

    const target = this.triggerElement || this.mainForm;
    if (!target) {
      root.classList.remove('sticky-atc--visible');
      return;
    }

    if (!this.hasUserScrolled) {
      root.classList.remove('sticky-atc--visible');
      return;
    }

    const targetRect = target.getBoundingClientRect();
    const intersectsViewport = targetRect.bottom > 0 && targetRect.top < winHeight;

    if (intersectsViewport) {
      this.hasSeenTrigger = true;
    }

    const passedTriggerUpwards = targetRect.top <= 0;
    const shouldShow = this.hasSeenTrigger && passedTriggerUpwards;

    if (shouldShow) {
      root.classList.add('sticky-atc--visible');
    } else {
      root.classList.remove('sticky-atc--visible');
    }
  }

  syncInitialQuantity() {
    if (!(this.refs.quantityInput instanceof HTMLInputElement)) return;

    if (this.mainQuantityInput && this.mainQuantityInput.value) {
      this.refs.quantityInput.value = this.mainQuantityInput.value;
    }
  }

  handleQuantityMinusClick(/** @type {Event} */ event) {
    event.preventDefault();
    if (!(this.refs.quantityInput instanceof HTMLInputElement)) return;

    const input = this.refs.quantityInput;
    const step = Number(input.step) || 1;
    const min = Number(input.min) || 1;
    let value = Number(input.value) || min;

    value = value - step;
    if (value < min) value = min;

    input.value = String(value);
    this.syncQuantityToMain();
  }

  handleQuantityPlusClick(/** @type {Event} */ event) {
    event.preventDefault();
    if (!(this.refs.quantityInput instanceof HTMLInputElement)) return;

    const input = this.refs.quantityInput;
    const step = Number(input.step) || 1;
    const min = Number(input.min) || 1;
    const max = Number(input.max) || Number.POSITIVE_INFINITY;
    let value = Number(input.value) || min;

    value = value + step;
    if (value > max) value = max;

    input.value = String(value);
    this.syncQuantityToMain();
  }

  handleQuantityInputChange() {
    this.syncQuantityToMain();
  }

  syncQuantityToMain() {
    if (!(this.refs.quantityInput instanceof HTMLInputElement) || !this.mainQuantityInput) return;

    const input = this.refs.quantityInput;
    const min = Number(input.min) || 1;
    const max = Number(input.max) || Number.POSITIVE_INFINITY;
    let value = Number(input.value) || min;

    if (value < min) value = min;
    if (value > max) value = max;

    input.value = String(value);
    this.mainQuantityInput.value = String(value);
    this.mainQuantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  handleQuantitySelectorUpdate(/** @type {CustomEvent} */ event) {
    if (!this.mainQuantityInput || !(this.refs.quantityInput instanceof HTMLInputElement)) return;
    if (event.target !== this.mainQuantityInput) return;

    const quantity = event.detail?.quantity;
    if (typeof quantity !== 'number') return;

    this.refs.quantityInput.value = String(quantity);
  }

  handleVariantUpdate(/** @type {CustomEvent} */ event) {
    const productId = event.detail?.data?.newProduct?.id || event.detail?.data?.productId;
    if (productId && this.productId && productId.toString() !== this.productId.toString()) return;

    const variant = event.detail.resource;
    if (!variant) return;

    if (variant.featured_media && variant.featured_media.preview_image && this.refs.thumbnail instanceof HTMLImageElement) {
      const src = variant.featured_media.preview_image.src;
      const url = src.includes('width=') ? src : `${src}&width=120`;
      this.refs.thumbnail.src = url;
    }

    if (this.refs.quantityInput instanceof HTMLInputElement && variant.quantity_rule) {
      const { min, max, increment } = variant.quantity_rule;
      if (min != null) this.refs.quantityInput.min = String(min);
      if (max != null) this.refs.quantityInput.max = String(max);
      if (increment != null) this.refs.quantityInput.step = String(increment);
      if (!this.refs.quantityInput.value) this.refs.quantityInput.value = String(min || 1);

      this.syncQuantityToMain();
    }
  }

  handleStickySubmit(/** @type {Event} */ event) {
    event.preventDefault();
    if (!(this.mainForm instanceof HTMLFormElement)) return;

    this.syncQuantityToMain();
    this.mainForm.requestSubmit();
  }

  handleCloseClick(/** @type {Event} */ event) {
    event.preventDefault();
    this.isClosed = true;
    this.updateVisibility();
  }
}

if (!customElements.get('sticky-atc-component')) {
  customElements.define('sticky-atc-component', StickyATCComponent);
}
